import { Cta } from "@/components/Cta";
import { MediaPlaceholder } from "@/components/MediaPlaceholder";
import { PageHero } from "@/components/PageHero";
import { Reveal } from "@/components/Reveal";
import { Section } from "@/components/Section";
import { SectionHeader } from "@/components/SectionHeader";
import { useSeo } from "@/lib/seo";
import { Container, Grid, Text, Title } from "@repo/ui/core";
import { IconCheck } from "@repo/ui/icons";
import styles from "@styles/site.module.css";

type FeatureRowProps = {
  title: string;
  description: string;
  bullets: string[];
  placeholder: string;
  placeholderKind?: "screenshot" | "loop";
  img?: string;
  caption: string;
  reverse?: boolean;
};

function FeatureText({
  title,
  description,
  bullets,
}: Omit<
  FeatureRowProps,
  "placeholder" | "placeholderKind" | "img" | "caption" | "reverse"
>) {
  return (
    <>
      <Title
        order={2}
        fw={650}
        size="2rem"
        lh={1.15}
        style={{ letterSpacing: "-0.02em" }}
      >
        {title}
      </Title>
      <Text size="lg" c="dimmed" lh={1.65} mt="sm">
        {description}
      </Text>
      <ul className={styles.checkList} style={{ marginTop: 20 }}>
        {bullets.map((bullet) => (
          <li key={bullet}>
            <IconCheck size={16} stroke={2.5} />
            {bullet}
          </li>
        ))}
      </ul>
    </>
  );
}

function FeatureMedia({
  placeholder,
  placeholderKind,
  img,
  caption,
  ratio = "16 / 10",
}: {
  placeholder: string;
  placeholderKind?: "screenshot" | "loop";
  img?: string;
  caption: string;
  ratio?: string;
}) {
  return (
    <div className={styles.mediaFrame}>
      <MediaPlaceholder
        label={placeholder}
        src={img}
        kind={placeholderKind}
        ratio={ratio}
      />
      <div className={styles.frameCaption}>{caption}</div>
    </div>
  );
}

function FeatureRow(props: FeatureRowProps) {
  const textCol = (
    <Grid.Col span={{ base: 12, md: 6 }}>
      <FeatureText {...props} />
    </Grid.Col>
  );
  const imageCol = (
    <Grid.Col span={{ base: 12, md: 6 }}>
      <FeatureMedia
        placeholder={props.placeholder}
        placeholderKind={props.placeholderKind}
        img={props.img}
        caption={props.caption}
      />
    </Grid.Col>
  );

  return (
    <Section hairline>
      <Reveal>
        <Grid gutter={64} align="center">
          {props.reverse ? (
            <>
              {imageCol}
              {textCol}
            </>
          ) : (
            <>
              {textCol}
              {imageCol}
            </>
          )}
        </Grid>
      </Reveal>
    </Section>
  );
}

export default function Emulators() {
  useSeo("/products/emulators");

  return (
    <>
      <PageHero
        eyebrow="Web Emulators"
        title={
          <>
            Cloud emulators in{" "}
            <span className={styles.gradientText}>your browser</span>
          </>
        }
        description="Connect to a running Android Automotive emulator from any browser tab. Multi-display IVI testing, GPS route playback, and full ADB and VHAL access, with no bench hardware on your desk."
      >
        <Cta to="/contact">Book a Demo</Cta>
      </PageHero>

      <FeatureRow
        title="Multi-display IVI testing"
        description="Interact with multiple displays in varying configurations. Test cluster, center stack, and passenger displays together, with microphone input and audio output for voice assistant testing, all from your browser."
        bullets={[
          "Configurable display layouts",
          "Touch and input simulation",
          "Real-time screen mirroring",
          "Microphone input for voice testing",
          "Audio output routing",
        ]}
        placeholder="Multi-display IVI session in the browser, cluster and center stack side by side, touch input being exercised"
        placeholderKind="loop"
        caption="Emulator / multi-display"
        img="/null/screenshot.controls.png"
      />

      <FeatureRow
        title="Debug without leaving the browser"
        description="Real-time device logs, ADB commands, and VHAL property management. Read and set VHAL properties to simulate real-world conditions."
        bullets={[
          "Live VHAL property editor",
          "Streaming device logs",
          "Full ADB shell access",
          "Sensor value simulation",
        ]}
        placeholder="VHAL property editor open on a live emulator, vehicle speed property being set"
        caption="VHAL / property editor"
        img="/null/screenshot.vhal.png"
        reverse
      />

      {/* Third feature: stacked, breaks the zigzag */}
      <Section hairline>
        <SectionHeader
          title="Drive anywhere without leaving your desk"
          description="Set the vehicle's GPS position by hand, or upload a spreadsheet of coordinates and play it back as a drive. Test navigation, charging-stop routing, and location-aware apps on a cloud emulator."
        />
        <Reveal>
          <ul
            className={styles.checkList}
            style={{
              gridTemplateColumns: "repeat(auto-fit, minmax(240px, 1fr))",
              marginBottom: 40,
            }}
          >
            {[
              "Enter latitude and longitude by hand",
              "Upload a spreadsheet of waypoints",
              "Play routes back as a simulated drive",
            ].map((bullet) => (
              <li key={bullet}>
                <IconCheck size={16} stroke={2.5} />
                {bullet}
              </li>
            ))}
          </ul>
        </Reveal>
        <Reveal delay={0.08} y={24}>
          <FeatureMedia
            placeholder="GPS panel playing back an uploaded route while the emulator's navigation map follows the vehicle"
            caption="GPS / route playback"
            ratio="21 / 9"
            img="/null/screenshot.gps.png"
          />
        </Reveal>
      </Section>

      {/* CTA */}
      <section className={styles.finalCta}>
        <Container size="xl" py={88} ta="center">
          <Reveal>
            <Title
              order={2}
              fw={650}
              size="2rem"
              style={{ letterSpacing: "-0.02em" }}
            >
              Your next emulator is a browser tab
            </Title>
            <Text
              size="lg"
              c="dimmed"
              maw={480}
              mx="auto"
              mt="md"
              mb="xl"
              lh={1.65}
            >
              See multi-display IVI testing and VHAL debugging live on a call.
            </Text>
            <div className={styles.ctaRow} style={{ justifyContent: "center" }}>
              <Cta to="/contact">Book a Demo</Cta>
            </div>
          </Reveal>
        </Container>
      </section>
    </>
  );
}
